import Input from "@/components/form/Input";
import Select, { arrayToOptions } from "@/components/form/Select";
import Textarea from "@/components/form/Textarea";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useForm as useFormspree, ValidationError } from "@formspree/react";
import { CheckCircle2 } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";

export const VALID_STATUSES = [
    "LATE_TO_START",
    "EMPTY_DIARY",
    "LATE_TO_LOG",
    "NEVER_WORKED",
    "PAIN_SPIKE",
    "ACTIVE",
    "PENDING",
    "COMPLETED",
    "DISCARDED",
    "PAIN_REDUCED",
    "NORMAL",
    "READY_TO_REVIEW",
    "NO_TRACKER",
    "LOW",
    "HIGH",
] as const;

const FORMSPREE_KEY = "mvgrlpnp";

// --- FeedbackForm1: For Patients Who Haven't Started ---
export function FeedbackForm1(
    { patientName, email }: { patientName?: string; email?: string },
) {
    const [state, handleSubmit] = useFormspree(FORMSPREE_KEY);
    const methods = useForm({ defaultValues: { email: email ?? "" } });
    if (state.succeeded) {
        return (
            <div className="flex flex-col justify-center items-center mx-auto">
                <div className="flex flex-col justify-center items-center bg-white shadow-lg mx-auto mt-8 p-8 border border-slate-200 rounded-xl max-w-lg">
                    <div className="flex justify-center items-center bg-gradient-to-tr from-emerald-100 to-green-200 mb-4 rounded-lg w-16 h-16">
                        <CheckCircle2 size={48} className="text-green-600" />
                    </div>
                    <h2 className="mb-2 font-bold text-green-700 text-2xl text-center">
                        Feedback Submitted!
                    </h2>
                    <p className="mb-2 text-slate-700 text-lg text-center">
                        Thank you for your feedback!
                    </p>
                    <p className="text-slate-500 text-center">
                        We appreciate your input and will use it to improve
                        Patient Watch for you and others.
                    </p>
                </div>
            </div>
        );
    }
    return (
        <FormProvider {...methods}>
            <form
                className="flex flex-col gap-4 mx-auto p-4 pb-8 max-w-lg"
                onSubmit={handleSubmit}
            >
                <div className="flex flex-col gap-4 pt-4">
                    <h2 className="font-bold text-xl">
                        Quick Check-In: Your Patient Watch Diary
                    </h2>
                    <p>
                        Hi{patientName ? ` ${patientName}` : ""}, this is Guy
                        from Patient Watch. We noticed you haven't started using
                        your diary yet. Your feedback helps us make Patient
                        Watch better for everyone, so we'd appreciate a quick
                        moment of your time.
                    </p>
                </div>
                {email
                    ? <input type="hidden" name="email" value={email} />
                    : (
                        <Input
                            name="email"
                            label="Email address"
                            type="email"
                            required
                            autoFocus
                        />
                    )}
                <Textarea
                    name="reason"
                    label="1. What was the main reason you haven't started using your Patient Watch diary?"
                    placeholder="E.g., I didn't receive the invitation, had trouble logging in, or got busy."
                    required
                />
                <ValidationError
                    prefix="Reason"
                    field="reason"
                    errors={state.errors}
                />
                <Textarea
                    name="improvements"
                    label="2. What's one thing we could have done differently to help you get started?"
                    placeholder="Tell us how we can improve for future users."
                />
                <ValidationError
                    prefix="Improvements"
                    field="improvements"
                    errors={state.errors}
                />
                <Button type="submit" disabled={state.submitting}>
                    Submit Feedback
                </Button>
            </form>
        </FormProvider>
    );
}

// --- FeedbackForm2: For Patients Who Have Slowed Down ---
export function FeedbackForm2(
    { patientName, email }: { patientName?: string; email?: string },
) {
    const [state, handleSubmit] = useFormspree(FORMSPREE_KEY);
    const methods = useForm({ defaultValues: { email: email ?? "" } });
    if (state.succeeded) {
        return (
            <div className="flex flex-col justify-center items-center bg-white shadow-lg mx-auto mt-8 p-8 border border-slate-200 rounded-xl max-w-lg">
                <div className="flex justify-center items-center bg-green-100 mb-4 rounded-full w-16 h-16">
                    <span className="text-green-600 text-4xl">✅</span>
                </div>
                <h2 className="mb-2 font-bold text-green-700 text-2xl text-center">
                    Feedback Submitted!
                </h2>
                <p className="mb-2 text-slate-700 text-lg text-center">
                    Thank you for your feedback!
                </p>
                <p className="text-slate-500 text-center">
                    We appreciate your input and will use it to improve Patient
                    Watch for you and others.
                </p>
            </div>
        );
    }
    return (
        <FormProvider {...methods}>
            <form
                className="flex flex-col gap-4 mx-auto p-4 pb-8 max-w-lg"
                onSubmit={handleSubmit}
            >
                <div className="flex flex-col gap-4 pt-4">
                    <h2 className="font-bold text-xl">
                        Quick Feedback: Your Patient Watch Diary
                    </h2>
                    <p>
                        Hi{patientName ? ` ${patientName}` : ""}, this is Guy
                        from Patient Watch. We noticed your diary activity has
                        slowed down. Your feedback helps us understand why and
                        improve the experience for you and others.
                    </p>
                </div>
                {email
                    ? <input type="hidden" name="email" value={email} />
                    : (
                        <Input
                            name="email"
                            label="Email address"
                            type="email"
                            required
                            autoFocus
                        />
                    )}
                <Textarea
                    name="reason_stopped"
                    label="1. What was the main reason you stopped or slowed down logging your diary entries?"
                    placeholder="E.g., I got too busy, my symptoms changed, technical issues, or the diary wasn't as helpful as I hoped."
                    required
                />
                <ValidationError
                    prefix="ReasonStopped"
                    field="reason_stopped"
                    errors={state.errors}
                />
                <Textarea
                    name="how_to_help_continue"
                    label="2. What could we have done to help you continue using your diary consistently?"
                    placeholder="Tell us what would have made a difference."
                />
                <ValidationError
                    prefix="HowToHelpContinue"
                    field="how_to_help_continue"
                    errors={state.errors}
                />
                <Button type="submit" disabled={state.submitting}>
                    Submit Feedback
                </Button>
            </form>
        </FormProvider>
    );
}

// --- FeedbackForm3: For Active Users (Superhuman PMF Style) ---
export function FeedbackForm3(
    { patientName, email }: { patientName?: string; email?: string },
) {
    const [state, handleSubmit] = useFormspree(FORMSPREE_KEY);
    const methods = useForm({ defaultValues: { email: email ?? "" } });
    if (state.succeeded) {
        return (
            <div className="flex flex-col justify-center items-center bg-white shadow-lg mx-auto mt-8 p-8 border border-slate-200 rounded-xl max-w-lg">
                <div className="flex justify-center items-center bg-green-100 mb-4 rounded-full w-16 h-16">
                    <span className="text-green-600 text-4xl">✅</span>
                </div>
                <h2 className="mb-2 font-bold text-green-700 text-2xl text-center">
                    Feedback Submitted!
                </h2>
                <p className="mb-2 text-slate-700 text-lg text-center">
                    Thank you for your feedback!
                </p>
                <p className="text-slate-500 text-center">
                    We appreciate your input and will use it to improve Patient
                    Watch for you and others.
                </p>
            </div>
        );
    }
    return (
        <FormProvider {...methods}>
            <form
                className="flex flex-col gap-4 mx-auto p-4 pb-8 max-w-lg"
                onSubmit={handleSubmit}
            >
                <div className="flex flex-col gap-4 pt-4">
                    <h2 className="font-bold text-xl">
                        Your Experience with Patient Watch
                    </h2>
                    <p>
                        Hi{patientName ? ` ${patientName}` : ""}, this is Guy
                        from Patient Watch. We're thrilled you're consistently
                        using your diary! As an active user, your insights are
                        incredibly valuable. Please share your thoughts on these
                        quick questions.
                    </p>
                </div>
                {email
                    ? <input type="hidden" name="email" value={email} />
                    : (
                        <Input
                            name="email"
                            label="Email address"
                            type="email"
                            required
                            autoFocus
                        />
                    )}
                <Select
                    name="disappointment_level"
                    label="1. How disappointed would you be if you could no longer use Patient Watch?"
                    required
                    options={arrayToOptions([
                        "Very disappointed",
                        "Somewhat disappointed",
                        "Not disappointed",
                    ])}
                />
                <ValidationError
                    prefix="DisappointmentLevel"
                    field="disappointment_level"
                    errors={state.errors}
                />
                <Textarea
                    name="main_benefit"
                    label="2. What is the main benefit you receive from Patient Watch?"
                    placeholder="Tell us what you find most valuable."
                    required
                />
                <ValidationError
                    prefix="MainBenefit"
                    field="main_benefit"
                    errors={state.errors}
                />
                <Textarea
                    name="how_to_improve"
                    label="3. What's one thing that could make Patient Watch even better for you?"
                    placeholder="Your suggestions help us improve!"
                />
                <ValidationError
                    prefix="HowToImprove"
                    field="how_to_improve"
                    errors={state.errors}
                />
                <Button type="submit" disabled={state.submitting}>
                    Submit Feedback
                </Button>
            </form>
        </FormProvider>
    );
}
